import React, { useState, useRef, useEffect } from 'react';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiCalendarCursor } from '@mdi/js';
import SingleMonthDatePicker from './SingleMonthDatePicker';
import './DateInputWithPicker.css';

/**
 * DateInputWithPicker - Reusable date input with calendar icon and dropdown picker
 *
 * A complete date selection component that combines:
 * - Readonly text input with formatted date display
 * - Calendar cursor icon on the right
 * - Portal-based SingleMonthDatePicker dropdown
 * - Automatic positioning and state management
 *
 * @param {Object} props
 * @param {string} props.id - Input field ID
 * @param {string} props.value - Date value (ISO string or empty)
 * @param {Function} props.onChange - Callback when date selected: (isoDateString) => void
 * @param {string} props.placeholder - Placeholder text (default: "Click to select")
 * @param {boolean} props.error - Whether input has error styling
 * @param {boolean} props.required - Whether field is required
 * @param {Object} props.style - Additional styles for input container
 * @param {DateTime} [props.fallbackMonth] - Luxon DateTime to open the picker on when
 *        this field has no value yet (e.g. an end-date picker opening on the chosen
 *        start date's month instead of the current month). Ignored once `value` is set.
 */
const DateInputWithPicker = ({
    id,
    value = '',
    onChange,
    placeholder = 'Click to select',
    error = false,
    required = false,
    style = {},
    disablePast = true,
    fallbackMonth = null
}) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const inputRef = useRef(null);

    const computePosition = () => {
        const rect = inputRef.current?.getBoundingClientRect();
        if (!rect) return null;
        return {
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX
        };
    };

    const handleOpenPicker = () => {
        const pos = computePosition();
        if (pos) {
            setPickerPosition(pos);
            setPickerOpen(true);
        }
    };

    // Keep the portalled calendar anchored to the input while open. The picker
    // portals to document.body with page coordinates, so a scrolling ancestor
    // (e.g. a modal body) moves the input without moving the calendar — the
    // capture-phase scroll listener catches inner scroll containers too.
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const reposition = () => {
            const pos = computePosition();
            if (pos) setPickerPosition(pos);
        };
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [pickerOpen]);

    const handleDateSelect = (range) => {
        onChange(range.start.toISODate());
        setPickerOpen(false);
    };

    return (
        <>
            <div className="date-input-with-picker" style={style}>
                <input
                    ref={inputRef}
                    type="text"
                    id={id}
                    value={value ? DateTime.fromISO(value).toFormat('MMM d, yyyy') : ''}
                    onClick={handleOpenPicker}
                    readOnly
                    placeholder={placeholder}
                    className={error ? 'error' : ''}
                    required={required}
                />
                <Icon
                    path={mdiCalendarCursor}
                    size={0.8}
                    style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#666'
                    }}
                />
            </div>

            <SingleMonthDatePicker
                isOpen={pickerOpen}
                position={pickerPosition}
                selectedRange={value ? {
                    start: DateTime.fromISO(value),
                    end: DateTime.fromISO(value)
                } : null}
                initialMonth={value ? DateTime.fromISO(value) : (fallbackMonth || null)}
                onSelectRange={handleDateSelect}
                onClose={() => setPickerOpen(false)}
                disablePast={disablePast}
            />
        </>
    );
};

export default DateInputWithPicker;
